<script setup>
/**
 * 加载页 Icon Cloud — 图标绕中心 Logo 3D 旋转，再收拢
 */
import { cn } from '@inspira-ui/plugins';
import { onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue';
import { BRAND_LOGO_SRC } from '@/config/brand.js';

const props = defineProps({
  class: { type: String, default: '' },
  images: { type: Array, default: () => [] },
  gathering: { type: Boolean, default: false },
  gathered: { type: Boolean, default: false },
  logoSrc: { type: String, default: BRAND_LOGO_SRC },
  gatherRoutes: {
    type: Array,
    default: () => [
      { midR: 0.3, duration: 820, delay: 0 },
      { midR: 0.22, duration: 1040, delay: 90 },
      { midR: 0.33, duration: 1120, delay: 25 },
      { midR: 0.17, duration: 780, delay: 145 },
      { midR: 0.25, duration: 980, delay: 55 },
      { midR: 0.31, duration: 1160, delay: 115 },
      { midR: 0.19, duration: 740, delay: 185 },
      { midR: 0.28, duration: 920, delay: 75 },
      { midR: 0.35, duration: 1200, delay: 15 },
    ],
  },
  size: { type: Number, default: 520 },
  /** 动画速率倍率，>1 加速，<1 放慢 */
  speedFactor: { type: Number, default: 1 },
});

const emit = defineEmits(['rotationLap']);

const canvasRef = ref(null);
const animationFrameRef = ref(0);
const logoImageRef = ref(null);
const imageCanvasesRef = ref([]);
const imagesLoadedRef = ref([]);
const imagePositions = ref([]);
const gatherStart = ref(0);
const burstStart = ref(0);
const rotation = reactive({ x: -0.52, y: 0 });

const ROTATION_STEP = 0.016;
const FULL_LAP = Math.PI * 2;
let rotationAccum = 0;
let lapEmitted = false;

const PRIMARY = '#155eef';

function sphereRadius(size) {
  return size * 0.36;
}

function logoRadius(size) {
  return size * 0.077;
}

function iconDrawSize(size) {
  return Math.round(size * 0.1);
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function gatherFactor(index, elapsed) {
  const route = props.gatherRoutes[index % props.gatherRoutes.length];
  const speed = Math.max(0.5, props.speedFactor);
  const scaledDelay = route.delay / speed;
  const scaledDuration = route.duration / speed;
  if (elapsed <= scaledDelay) return 1;

  const t = Math.min(1, (elapsed - scaledDelay) / scaledDuration);
  const eased = easeInOutCubic(t);

  if (eased <= 0.56) {
    return 1 - (1 - route.midR) * (eased / 0.56);
  }
  return route.midR * (1 - (eased - 0.56) / 0.44);
}

function projectIcon(icon, factor, rotX, rotY) {
  const px = icon.x * factor;
  const py = icon.y * factor;
  const pz = icon.z * factor;

  const cosX = Math.cos(rotX);
  const sinX = Math.sin(rotX);
  const cosY = Math.cos(rotY);
  const sinY = Math.sin(rotY);

  const rotatedX = px * cosY - pz * sinY;
  const rotatedZ = px * sinY + pz * cosY;
  const rotatedY = py * cosX + rotatedZ * sinX;

  return { rotatedX, rotatedY, rotatedZ };
}

function drawIcon(ctx, index, projected, factor, size) {
  const sphereR = sphereRadius(size);
  const iconSize = iconDrawSize(size);
  const half = iconSize / 2;
  const { rotatedX, rotatedY, rotatedZ } = projected;
  const depthScale = (rotatedZ + sphereR + 90) / (sphereR + 150);
  const depthOpacity = Math.max(0.3, Math.min(1, (rotatedZ + sphereR * 0.65) / (sphereR * 0.95)));
  const gatherScale = 0.22 + factor * 0.78;
  const gatherOpacity = factor < 0.06 ? 0 : depthOpacity * Math.min(1, factor * 1.3);
  const scale = depthScale * gatherScale * 1.08;

  ctx.save();
  ctx.translate(rotatedX, rotatedY);
  ctx.scale(scale, scale);
  ctx.globalAlpha = gatherOpacity;

  if (imageCanvasesRef.value[index] && imagesLoadedRef.value[index]) {
    ctx.drawImage(imageCanvasesRef.value[index], -half, -half, iconSize, iconSize);
  }
  ctx.restore();
}

function drawLogo(ctx, centerX, centerY, now, size) {
  const logoR = logoRadius(size);
  // 收拢完成后的爆发环
  if (props.gathered && burstStart.value) {
    const burstMs = 380 / Math.max(0.5, props.speedFactor);
    const t = Math.min(1, (now - burstStart.value) / burstMs);
    const burstR = logoR + t * logoR * 1.6;
    ctx.save();
    ctx.globalAlpha = 0.45 * (1 - t);
    ctx.beginPath();
    ctx.arc(centerX, centerY, burstR, 0, Math.PI * 2);
    ctx.fillStyle = `${PRIMARY}40`;
    ctx.fill();
    ctx.restore();
  }

  // 脉冲环（旋转阶段）
  if (!props.gathering && !props.gathered) {
    const pulse = 0.5 + Math.sin(now * 0.004) * 0.5;
    ctx.save();
    ctx.globalAlpha = 0.18 * (1 - pulse);
    ctx.beginPath();
    ctx.arc(centerX, centerY, logoR + 8 + pulse * 14, 0, Math.PI * 2);
    ctx.fillStyle = PRIMARY;
    ctx.fill();
    ctx.restore();
  }

  // 圆形 Logo 图片
  ctx.save();
  ctx.shadowColor = `${PRIMARY}55`;
  ctx.shadowBlur = 22;

  if (logoImageRef.value) {
    const img = logoImageRef.value;
    const diameter = logoR * 2;
    const imgRatio = img.width / img.height;
    let drawW;
    let drawH;
    let drawX;
    let drawY;

    if (imgRatio > 1) {
      drawH = diameter;
      drawW = diameter * imgRatio;
      drawX = centerX - drawW / 2;
      drawY = centerY - logoR;
    } else {
      drawW = diameter;
      drawH = diameter / imgRatio;
      drawX = centerX - logoR;
      drawY = centerY - drawH / 2;
    }

    ctx.beginPath();
    ctx.arc(centerX, centerY, logoR, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
  } else {
    ctx.beginPath();
    ctx.arc(centerX, centerY, logoR, 0, Math.PI * 2);
    ctx.fillStyle = PRIMARY;
    ctx.fill();
  }
  ctx.restore();
}

watch(() => props.gathering, (val) => {
  if (val) gatherStart.value = performance.now();
});

watch(() => props.gathered, (val) => {
  if (val) burstStart.value = performance.now();
});

watchEffect(() => {
  if (!props.logoSrc) return;

  const img = new Image();
  img.src = props.logoSrc;
  img.onload = () => {
    logoImageRef.value = img;
  };
});

watchEffect(() => {
  const images = props.images;
  if (!images?.length) return;

  const iconSize = iconDrawSize(props.size);
  const half = iconSize / 2;

  imagesLoadedRef.value = new Array(images.length).fill(false);
  imageCanvasesRef.value = images.map((url, idx) => {
    const offscreen = document.createElement('canvas');
    offscreen.width = iconSize;
    offscreen.height = iconSize;
    const offCtx = offscreen.getContext('2d');
    if (!offCtx) return offscreen;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    img.onload = () => {
      offCtx.clearRect(0, 0, iconSize, iconSize);
      offCtx.beginPath();
      offCtx.arc(half, half, half, 0, Math.PI * 2);
      offCtx.closePath();
      offCtx.clip();
      offCtx.drawImage(img, 0, 0, iconSize, iconSize);
      imagesLoadedRef.value[idx] = true;
    };
    return offscreen;
  });
});

watchEffect(() => {
  const count = props.images?.length || 0;
  if (!count) {
    imagePositions.value = [];
    return;
  }

  const sphereR = sphereRadius(props.size);
  const offset = 2 / count;
  const increment = Math.PI * (3 - Math.sqrt(5));
  const positions = [];

  for (let i = 0; i < count; i++) {
    const y = i * offset - 1 + offset / 2;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const phi = i * increment;
    positions.push({
      x: Math.cos(phi) * r * sphereR,
      y: y * sphereR,
      z: Math.sin(phi) * r * sphereR,
      id: i,
    });
  }
  imagePositions.value = positions;
});

onMounted(() => {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const logical = props.size;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = logical * dpr;
  canvas.height = logical * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  function animate(now) {
    ctx.clearRect(0, 0, logical, logical);

    const centerX = logical / 2;
    const centerY = logical / 2;
    const elapsed = props.gathering ? now - gatherStart.value : 0;

    if (!props.gathering) {
      const step = ROTATION_STEP * Math.max(0.5, props.speedFactor);
      rotation.y += step;
      rotation.x = -0.52 + Math.sin(now * 0.00055) * 0.12;

      rotationAccum += step;
      if (!lapEmitted && rotationAccum >= FULL_LAP) {
        lapEmitted = true;
        emit('rotationLap');
      }
    }

    const projected = imagePositions.value.map((icon, index) => {
      const factor = props.gathering ? gatherFactor(index, elapsed) : 1;
      const p = projectIcon(icon, factor, rotation.x, rotation.y);
      return { index, factor, ...p };
    });

    projected.sort((a, b) => a.rotatedZ - b.rotatedZ);

    const behind = projected.filter((p) => p.rotatedZ < 0);
    const front = projected.filter((p) => p.rotatedZ >= 0);

    ctx.save();
    ctx.translate(centerX, centerY);

    for (const item of behind) {
      drawIcon(ctx, item.index, item, item.factor, logical);
    }

    ctx.restore();
    drawLogo(ctx, centerX, centerY, now, logical);

    ctx.save();
    ctx.translate(centerX, centerY);

    for (const item of front) {
      drawIcon(ctx, item.index, item, item.factor, logical);
    }

    ctx.restore();

    animationFrameRef.value = requestAnimationFrame(animate);
  }

  animationFrameRef.value = requestAnimationFrame(animate);
});

onBeforeUnmount(() => {
  if (animationFrameRef.value) cancelAnimationFrame(animationFrameRef.value);
});
</script>

<template>
  <canvas
    ref="canvasRef"
    role="img"
    aria-label="Loading"
    :class="cn('pointer-events-none size-full max-w-none', props.class)"
  />
</template>
